/**
 * caseService — CRM case management
 *
 * Architecture:
 * - OUTBOUND (MRT → CRM): All contact form submissions create a case via POST
 * - INBOUND (CRM → MRT): Status updates come back via webhook/API callback
 *
 * All 11 contact form topics submit through createCase().
 * Cases appear in Cases tab under the appropriate status.
 *
 * Mock data sourced from BrightStars Incorp. demo customer (demoData.js).
 */
import { DEMO_CASES } from '@data/demoData';

/**
 * Create a new case from any contact form submission
 * @param {string} topicId - One of the 11 CONTACT_TOPICS ids
 * @param {Object} formData - { product, message, quantity, orderNumber, etc. }
 * @param {Object} contactDetails - { firstName, lastName, email, phone, company, country, postalCode }
 * @returns {Promise<Object>} { caseId, caseNumber, status }
 */
export async function createCase(topicId, formData, contactDetails) {
  const mockCase = {
    caseId: `case-${Date.now()}`,
    caseNumber: `CS-${Date.now().toString().slice(-5)}`,
    status: 'new',
    topicId,
    subject: formData.subject || formData.message?.slice(0, 50) || topicId,
    createdDate: new Date().toISOString(),
    lastUpdated: new Date().toISOString(),
  };
  console.log('[caseService] Case created (stub):', mockCase);
  return mockCase;
}

/**
 * Get all cases for the current customer account
 * @param {Object} [filters] - { status, dateFrom, dateTo }
 * @returns {Promise<Array>} Array of case objects
 */
export async function getCases(filters = {}) {
  // Simulate network delay for realistic loading state
  await new Promise(r => setTimeout(r, 300));
  return DEMO_CASES;
}

/**
 * Get case detail with message thread
 * @param {string} caseId
 * @returns {Promise<Object>} Full case with messages array
 */
export async function getCaseDetail(caseId) {
  const cases = await getCases();
  const found = cases.find(c => c.caseId === caseId);
  return {
    ...found,
    messages: [],
  };
}

/**
 * INBOUND: Update case status (called by webhook from external CRM)
 * @param {string} caseId
 * @param {Object} update - { status, comment, updatedBy, timestamp }
 * @returns {Promise<Object>} Updated case
 */
export async function updateCaseFromCRM(caseId, update) {
  console.log('[caseService] CRM webhook update (stub):', { caseId, ...update });
  return { caseId, ...update, applied: true };
}
